import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Logo from "../../../components/Logo";
import { logout } from "../../../components/LogOut";
import { openEmailFeedback } from "../../../components/FeedBack";
import { User, Mail, LogOut } from "lucide-react";
import { useUser } from "src/shared/context/UserContext";
import OpenCloseDayPopup from "../../admin/shift/OpenCloseDayPopup";
import api from "src/features/auth/axiosConfig";
import { notifyWarning } from "src/assets/toastService";
import { DoorClosed, CalendarX } from "lucide-react";
import { useBusinessOperations } from "../../admin/shift/useBusinessOperations";

interface Props {
  profileName: string;
  username?: string;
}

// Helper function to extract ID string from MongoDB ObjectId format
const extractId = (id: any): string | null => {
  if (!id) return null;
  if (typeof id === "string") return id;
  if (id.$oid) return id.$oid;
  if (id._id) return extractId(id._id);
  return null;
};

const ProfileSection: React.FC<Props> = ({ profileName, username }) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [closeDayVisible, setCloseDayVisible] = useState(false);
  const [activeBusinessId, setActiveBusinessId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [shouldLogout, setShouldLogout] = useState(false);

  const isFetchingRef = useRef(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { user, userConfigration } = useUser();
  const { currentStatus, refreshStatus } = useBusinessOperations();

  // Listen for custom shift status change events
  useEffect(() => {
    const handleShiftStatusChange = (event: CustomEvent) => {
      refreshStatus();

      // If status is OPEN, reset activeBusinessId to trigger fetch
      if (event.detail.status === "OPEN") {
        setActiveBusinessId(null);
      }
    };

    window.addEventListener("shiftStatusChanged", handleShiftStatusChange as EventListener);

    return () => {
      window.removeEventListener("shiftStatusChanged", handleShiftStatusChange as EventListener);
    };
  }, [refreshStatus]);

  // Refresh status on mount and when popup closes
  useEffect(() => {
    refreshStatus();
  }, [refreshStatus, closeDayVisible]);

  // Convert shiftEnabled safely to boolean
  // const toBoolean = (value: any) => value === true || value === "true";
  // const shiftEnabled = toBoolean(userConfigration?.businessOperationEnable);

  // Close dropdown if clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle logout when print is complete
  useEffect(() => {
    if (shouldLogout) {
      logout(navigate);
    }
  }, [shouldLogout, navigate]);

  // Fetch active shift ID when status is OPEN
  useEffect(() => {
    const fetchActiveShiftId = async () => {
      // Prevent multiple fetches
      if (isFetchingRef.current) return;

      // Check if currentStatus is exactly "OPEN" (not null or "CLOSED")
      if (currentStatus === "OPEN") {
        try {
          isFetchingRef.current = true;
          const response = await api.get("/app/pos/v2/business_operations?status=OPEN");
          const operations = response.data?.result || response.data;

          // Find the most recent OPEN operation
          const openBusiness = operations.find((op: any) => op.status === "OPEN");

          if (openBusiness && openBusiness._id) {
            const businessId = extractId(openBusiness._id);
            setActiveBusinessId(businessId);
            localStorage.setItem("activeShiftId", businessId || "");
          } else {
            setActiveBusinessId(null);
            localStorage.removeItem("activeShiftId");
          }
        } catch (error) {
          console.error("Error fetching active shift ID:", error);
          setActiveBusinessId(null);
        } finally {
          isFetchingRef.current = false;
        }
      } else {
        // Status is either "CLOSED" or null
        setActiveBusinessId(null);
        localStorage.removeItem("activeShiftId");
      }
    };

    fetchActiveShiftId();
  }, [currentStatus]); // Remove activeBusinessId from dependencies

  // Handle Close Day/Shift button click
  const handleCloseClick = () => {
    if (!activeBusinessId) {
      notifyWarning("No open shift found to close.");
      return;
    }
    setCloseDayVisible(true);
  };

  // Handle Logout click
  const handleLogoutClick = async () => {
    setDropdownVisible(false);
    logout(navigate);
  };

  // Close day success handler
  const handlePrintComplete = () => {
    setIsPrinting(false);
    logout(navigate);
  };

  // Close day success handler
  const handleCloseSuccess = () => {
    setCloseDayVisible(false);
    setActiveBusinessId(null);
    setIsPrinting(true);

    // Dispatch event to notify other components
    window.dispatchEvent(
      new CustomEvent("shiftStatusChanged", {
        detail: { status: "CLOSED" },
      }),
    );
  };

  // Close day cancel handler
  const handleCloseCancel = () => {
    setCloseDayVisible(false);
  };

  // Determine if we should show the close button
  const shouldShowCloseButton = user?.businessType === "Restaurant" && currentStatus === "OPEN" && activeBusinessId !== null;

  return (
    <div className="relative" ref={dropdownRef}>
      {shouldShowCloseButton && (
        <Tooltip title={userConfigration?.businessOperationType === "Shift" ? "Close current shift" : "Close business day"} arrow>
          <button
            onClick={handleCloseClick}
            disabled={isLoading || isPrinting || isFetchingRef.current}
            className="relative top-1.5 inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              <span className="animate-spin text-base leading-none">⌛</span>
            ) : isPrinting ? (
              <span className="animate-spin text-base leading-none">🖨️</span>
            ) : (
              <>
                {userConfigration?.businessOperationType === "Shift" ? <DoorClosed className="w-4 h-4 align-middle" /> : <CalendarX className="w-4 h-4 align-middle" />}
                <span className="leading-none">{userConfigration?.businessOperationType === "Shift" ? "Close Shift" : "Close Day"}</span>
              </>
            )}
          </button>
        </Tooltip>
      )}

      {/* Profile Icon */}
      <IconButton className="flex items-center gap-2 p-2 rounded-full" onClick={() => setDropdownVisible((prev) => !prev)} disabled={isLoading || isPrinting || isFetchingRef.current}>
        <Tooltip title={profileName} arrow>
          <div className="logo_header cursor-pointer">
            <Logo username={username} />
          </div>
        </Tooltip>
      </IconButton>

      {/* Dropdown Menu */}
      {dropdownVisible && (
        <div className="absolute right-0 mt-1 w-48 bg-[#F8F9FA] shadow-2xl rounded-xl z-50 overflow-hidden ring-1 ring-black ring-opacity-5">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Signed in as</p>
            <p className="text-sm font-semibold text-gray-800 truncate">{profileName}</p>
          </div>

          {/* Menu Items */}
          <div className="flex flex-col">
            <Link to="/v1/profile" className="flex items-center gap-2 px-4 py-3 hover:bg-gray-100 transition-all duration-150 transform hover:translate-x-1" onClick={() => setDropdownVisible(false)}>
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">Profile</span>
            </Link>

            <Link
              to="#"
              className="flex items-center gap-2 px-4 py-3 hover:bg-gray-100 transition-all duration-150 transform hover:translate-x-1"
              onClick={() => {
                openEmailFeedback("metanic1234@gmail.com", profileName);
                setDropdownVisible(false);
              }}
            >
              <Mail className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">Feedback</span>
            </Link>
          </div>

          <div className="border-t border-gray-200"></div>

          {/* Logout */}
          <div className="flex flex-col">
            <Link
              to="#"
              className={`flex items-center gap-2 px-4 py-3 hover:bg-gray-100 transition-all duration-150 transform hover:translate-x-1 ${isLoading || isPrinting || isFetchingRef.current ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={handleLogoutClick}
            >
              <LogOut className="w-4 h-4 text-red-600" />
              <span className="text-red-600">Logout</span>
            </Link>
          </div>
        </div>
      )}

      {/* CLOSE DAY Popup */}
      {closeDayVisible && activeBusinessId && (
        <OpenCloseDayPopup
          mode="close"
          isOpen={closeDayVisible}
          businessId={activeBusinessId || undefined}
          onClose={handleCloseCancel}
          onSuccess={handleCloseSuccess}
          onPrintComplete={handlePrintComplete}
        />
      )}
    </div>
  );
};

export default ProfileSection;
